import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import { replaceAccount } from "@/entities/accounts/table";
import { pauseAccount } from "api/accounts";
import { getUserFriendlyErrorMessage } from "api/UserFriendlyError";
import { getAccountDisplayName } from "@/entities/accounts";
import { ReduxRootState } from "@/types/redux-store";
import { AccountType } from "shared/types/accounts";

export type PauseAccountState = {
  pending: boolean;
};

const featureKey = "pauseAccount" as const;

const initialState: PauseAccountState = {
  pending: false,
};

function getFeatureState(state: ReduxRootState): PauseAccountState {
  return (
    (state as ReduxRootState & { [featureKey]?: PauseAccountState })[
      featureKey
    ] ?? initialState
  );
}

export const pauseAccountFx = createAsyncThunk(
  "pauseAccount/run",
  async (account: AccountType, thunkApi) => {
    try {
      const updatedAccount = await pauseAccount(account);

      thunkApi.dispatch(replaceAccount(updatedAccount));
      toast.success(
        `Posting for ${getAccountDisplayName(
          account
        )} has been successfully paused.`
      );

      return updatedAccount;
    } catch (error) {
      toast.error(getUserFriendlyErrorMessage(error));
      throw error;
    }
  }
);

const pauseAccountSlice = createSlice({
  name: featureKey,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(pauseAccountFx.pending, (state) => {
      state.pending = true;
    });
    builder.addCase(pauseAccountFx.fulfilled, (state) => {
      state.pending = false;
    });
    builder.addCase(pauseAccountFx.rejected, (state) => {
      state.pending = false;
    });
  },
});

export const pauseAccountReducer = pauseAccountSlice.reducer;

export const selectPauseAccountPending = createSelector(
  getFeatureState,
  (pauseAccountState) => pauseAccountState.pending
);
